// src/NewUPT/Security/CaptchaGate.jsx
import React from "react";

/**
 * Props:
 *  - challenge: { challengeId, type, hmac, payload?: { width, height, hint, imageUrl? } }
 *  - onPassed: () => void
 *  - onRefresh?: (newChallenge) => void
 */
export default function CaptchaGate({ challenge, onPassed, onRefresh }) {
  // ---- hooks first (unconditional) ----
  const [angle, setAngle] = React.useState(0);
  const [x, setX] = React.useState(null);
  const [y, setY] = React.useState(null);
  const [imgSrc, setImgSrc] = React.useState("");
  const imgRef = React.useRef(null);

  // derive safe payload defaults even when challenge is missing
  const payload = challenge?.payload || {};
  const width  = payload.width  ?? 320;
  const height = payload.height ?? 180;
  const hint   = payload.hint   ?? "Rotate upright";

  // Build a robust image URL with cache-buster and fallback.
  React.useEffect(() => {
    if (!challenge) return; // guard inside the effect (not before hooks)

    const base =
      payload.imageUrl ||
      `/api/patientinfo/captcha/render/${challenge.challengeId}.png`;
    const bust = challenge.hmac ? challenge.hmac.slice(-8) : Date.now();
    setImgSrc(`${base}?v=${bust}`);
    // reset local state for each new challenge
    setAngle(0);
    setX(null);
    setY(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge]); // depend on whole object so it updates on refresh

  // ---- after all hooks, it's safe to early-return UI ----
  if (!challenge) return null;

  const handleImgError = () => {
    const fallback = `/captcha/images/${challenge.challengeId}.png`;
    const bust = challenge.hmac ? challenge.hmac.slice(-8) : Date.now();
    if (!imgSrc.includes("/captcha/images/")) {
      setImgSrc(`${fallback}?v=${bust}`);
    }
  };

  const onClickImage = (e) => {
    if (!imgRef.current) return;
    const r = imgRef.current.getBoundingClientRect();
    setX(Math.round(e.clientX - r.left));
    setY(Math.round(e.clientY - r.top));
  };

  const verify = async () => {
    const body = { challengeId: challenge.challengeId, hmac: challenge.hmac };

    if (challenge.type === "rotate") body.angle = Math.round(angle) % 360;
    if (challenge.type === "slider") body.x = x ?? 0;
    if (challenge.type === "click") {
      body.x = x ?? 0;
      body.y = y ?? 0;
    }

    const res = await fetch("/api/patientinfo/captcha/verify?debug=1", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });

    const out = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.error("[CaptchaGate] verify FAILED", {
        ok: false,
        payloadSent: body,
        raw: out,
      });
      alert(out?.reason || "Captcha failed. Try again.");
      return;
    }

    console.log("[CaptchaGate] verify OK", out);
    onPassed?.();
  };

  const refresh = async () => {
    if (!onRefresh) return;
    try {
      const res = await fetch("/api/patientinfo/captcha/new", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ appKey: "default-api", type: "rotate" }),
      });
      const ch = await res.json();
      onRefresh(ch);
    } catch {
      alert("Could not load a new challenge. Please try again.");
    }
  };

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        padding: 12,
        borderRadius: 12,
        maxWidth: width,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        Security check: {String(challenge.type || "rotate").toUpperCase()}
      </div>

      <div
        style={{
          width,
          height,
          borderRadius: 8,
          overflow: "hidden",
          display: "inline-block",
          background: "#f7fafc",
        }}
      >
        <img
          ref={imgRef}
          src={imgSrc}
          width={width}
          height={height}
          alt="captcha"
          draggable="false"
          onError={handleImgError}
          onClick={challenge.type === "click" ? onClickImage : undefined}
          style={{
            display: "block",
            userSelect: "none",
            transform:
              challenge.type === "rotate" ? `rotate(${-angle}deg)` : "none",
            transformOrigin: "50% 50%",
            transition: "transform 60ms linear",
          }}
        />
      </div>

      <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>{hint}</div>

      {challenge.type === "rotate" && (
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 6 }}>Rotate upright</div>
          <input
            type="range"
            min="0"
            max="359"
            step="1"
            value={angle}
            onChange={(e) => setAngle(parseInt(e.target.value, 10))}
            style={{ width }}
          />
          <div>Angle: {Math.round(angle)}°</div>
        </div>
      )}

      {challenge.type === "slider" && (
        <div style={{ marginTop: 8 }}>
          <input
            type="range"
            min="0"
            max={width}
            step="1"
            value={x ?? 0}
            onChange={(e) => setX(parseInt(e.target.value, 10))}
            style={{ width }}
          />
          <div>X: {x ?? 0}px</div>
        </div>
      )}

      {challenge.type === "click" && x != null && y != null && (
        <div style={{ marginTop: 8 }}>Click @ ({x}, {y})</div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button onClick={verify}>Verify</button>
        {onRefresh && (
          <button type="button" onClick={refresh}>
            Try another
          </button>
        )}
      </div>
    </div>
  );
}
